import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { notificacionAPI } from '../lib/api';
import { cn, getRolLabel } from '../lib/utils';
import { Button } from './ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import PWAInstallBanner from './PWAInstallBanner';
import { useGuiasPushSubscription } from '../hooks/useGuiasPushSubscription';
import {
    FileText,
    Home,
    Users,
    Settings,
    Bell,
    LogOut,
    Menu,
    Sun,
    Moon,
    Shield,
    ClipboardCheck,
    Upload,
    History,
    Building2,
    User,
    X,
    BarChart3,
} from 'lucide-react';

const navItems = {
    admin: [
        { href: '/guias/dashboard', label: 'Dashboard', icon: Home },
        { href: '/guias/todos', label: 'Guías', icon: FileText },
        { href: '/guias/reportes', label: 'Reportes', icon: BarChart3 },
        { href: '/guias/usuarios', label: 'Usuarios', icon: Users },
        { href: '/guias/carga-masiva', label: 'Carga Masiva', icon: Upload },
        { href: '/guias/configuracion', label: 'Configuración', icon: Settings },
        { href: '/guias/historial', label: 'Historial', icon: History },
    ],
    solicitante: [
        { href: '/guias/dashboard', label: 'Dashboard', icon: Home },
        { href: '/guias/crear', label: 'Nueva Guía', icon: FileText },
        { href: '/guias/mis-guias', label: 'Mis Guías', icon: ClipboardCheck },
    ],
    gerente: [
        { href: '/guias/dashboard', label: 'Dashboard', icon: Home },
        { href: '/guias/aprobar', label: 'Aprobar Guías', icon: ClipboardCheck },
        { href: '/guias/todos', label: 'Todas las Guías', icon: FileText },
        { href: '/guias/reportes', label: 'Reportes', icon: BarChart3 },
        { href: '/guias/historial', label: 'Historial', icon: History },
    ],
    seguridad: [
        { href: '/guias/todos', label: 'Todas las Guías', icon: FileText },
        { href: '/guias/punto-salida', label: 'Punto de Salida', icon: Shield },
        { href: '/guias/reportes', label: 'Reportes', icon: BarChart3 },
    ],
    superadmin: [
        { href: '/guias/dashboard', label: 'Dashboard', icon: Home },
        { href: '/guias/todos', label: 'Guías', icon: FileText },
        { href: '/guias/reportes', label: 'Reportes', icon: BarChart3 },
        { href: '/guias/aprobar', label: 'Aprobar', icon: ClipboardCheck },
        { href: '/guias/historial', label: 'Historial', icon: History },
        { href: '/guias/configuracion', label: 'Configuración', icon: Settings },
    ],
    tecnico: [
        { href: '/guias/dashboard', label: 'Dashboard', icon: Home },
        { href: '/guias/todos', label: 'Guías', icon: FileText },
    ],
};

export const Layout = ({ children }) => {
    const { user, logout } = useAuth();
    const { theme, toggleTheme, isDark } = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [mobileOpen, setMobileOpen] = useState(false);

    useEffect(() => {
        loadNotifications();
        const interval = setInterval(loadNotifications, 30000);
        return () => clearInterval(interval);
    }, []);

    const loadNotifications = async () => {
        try {
            const response = await notificacionAPI.getAll();
            setNotifications(response.data);
            setUnreadCount(response.data.filter(n => !n.leida).length);
        } catch (err) {
            console.error('Error loading notifications:', err);
        }
    };

    const handleMarkAllRead = async () => {
        try {
            await notificacionAPI.markAllRead();
            loadNotifications();
        } catch (err) {
            console.error('Error marking notifications as read:', err);
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const goHome = () => {
        navigate('/home');
    };

    const currentNavItems = navItems[user?.rol] || [];

    const NavLink = ({ item, mobile = false }) => {
        const isActive = location.pathname === item.href;
        const Icon = item.icon;
        
        return (
            <Link
                to={item.href}
                onClick={() => mobile && setMobileOpen(false)}
                className={cn(
                    'flex items-center gap-3 px-4 py-3 rounded-sm font-medium transition-colors',
                    'hover:bg-primary/10 hover:text-primary',
                    isActive && 'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground',
                    !isActive && 'text-muted-foreground'
                )}
                data-testid={`nav-${item.href.replace('/', '')}`}
            >
                <Icon className="h-5 w-5" />
                <span className="uppercase text-sm tracking-wider">{item.label}</span>
            </Link>
        );
    };

    return (
        <div className="min-h-screen bg-background">
            {/* Mobile Header */}
            <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-card border-b-2 border-border h-16">
                <div className="flex items-center justify-between h-full px-4">
                    <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                        <SheetTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                                <Menu className="h-6 w-6" />
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="left" className="w-72 p-0">
                            <div className="flex flex-col h-full">
                                <div className="p-4 border-b-2 border-border cursor-pointer" onClick={goHome}>
                                    <h2 className="font-heading font-black text-xl uppercase tracking-tight text-primary">
                                        Multisistemas
                                    </h2>
                                    <p className="text-xs text-muted-foreground uppercase tracking-wider mt-1">
                                        Guías de Despacho
                                    </p>
                                </div>
                                <ScrollArea className="flex-1 p-2">
                                    <nav className="space-y-1">
                                        {currentNavItems.map((item) => (
                                            <NavLink key={item.href} item={item} mobile />
                                        ))}
                                    </nav>
                                </ScrollArea>
                                {/* User info and logout for mobile */}
                                <div className="p-4 border-t-2 border-border">
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
                                            <User className="h-5 w-5 text-primary" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-sm truncate">{user?.nombre}</p>
                                            <p className="text-xs text-muted-foreground uppercase">{getRolLabel(user?.rol)}</p>
                                        </div>
                                    </div>
                                    <Button
                                        variant="outline"
                                        className="w-full justify-start gap-2 text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
                                        onClick={() => {
                                            setMobileOpen(false);
                                            handleLogout();
                                        }}
                                        data-testid="logout-btn-mobile"
                                    >
                                        <LogOut className="h-4 w-4" />
                                        Cerrar Sesión
                                    </Button>
                                </div>
                            </div>
                        </SheetContent>
                    </Sheet>

                    <h1 className="font-heading font-black text-lg uppercase tracking-tight text-primary cursor-pointer" onClick={goHome}>
                        Multisistemas
                    </h1>

                    <div className="flex items-center gap-2">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={toggleTheme}
                            data-testid="theme-toggle-mobile"
                        >
                            {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                        </Button>
                    </div>
                </div>
            </header>

            {/* Desktop Sidebar */}
            <aside className="hidden lg:flex fixed left-0 top-0 bottom-0 w-64 bg-card border-r-2 border-border flex-col z-40">
                <div className="p-6 border-b-2 border-border cursor-pointer" onClick={goHome}>
                    <h2 className="font-heading font-black text-2xl uppercase tracking-tight text-primary">
                        Multisistemas
                    </h2>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mt-1">
                        Guías de Despacho
                    </p>
                </div>

                <ScrollArea className="flex-1 p-3">
                    <nav className="space-y-1">
                        {currentNavItems.map((item) => (
                            <NavLink key={item.href} item={item} />
                        ))}
                    </nav>
                </ScrollArea>

                <div className="p-4 border-t-2 border-border">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
                            <User className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{user?.nombre}</p>
                            <p className="text-xs text-muted-foreground uppercase">{getRolLabel(user?.rol)}</p>
                        </div>
                    </div>
                    <Button
                        variant="outline"
                        className="w-full justify-start gap-2 text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
                        onClick={handleLogout}
                        data-testid="logout-btn"
                    >
                        <LogOut className="h-4 w-4" />
                        Cerrar Sesión
                    </Button>
                </div>
            </aside>

            {/* Main Content Area */}
            <div className="lg:ml-64">
                {/* Desktop Top Bar */}
                <header className="hidden lg:flex fixed top-0 right-0 left-64 z-30 h-16 bg-card/80 backdrop-blur-sm border-b-2 border-border items-center justify-between px-6">
                    <div className="flex items-center gap-2">
                        <Building2 className="h-5 w-5 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                            {user?.empresa_id ? 'Empresa Demo S.A.' : 'Sin empresa'}
                        </span>
                    </div>

                    <div className="flex items-center gap-3">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={toggleTheme}
                            data-testid="theme-toggle"
                        >
                            {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                        </Button>

                        {/* Notifications */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                                    <Bell className="h-5 w-5" />
                                    {unreadCount > 0 && (
                                        <span className="notification-badge">{unreadCount}</span>
                                    )}
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-80">
                                <DropdownMenuLabel className="flex items-center justify-between">
                                    <span>Notificaciones</span>
                                    {unreadCount > 0 && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={handleMarkAllRead}
                                            className="text-xs"
                                        >
                                            Marcar todas leídas
                                        </Button>
                                    )}
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <ScrollArea className="h-64">
                                    {notifications.length === 0 ? (
                                        <div className="p-4 text-center text-muted-foreground text-sm">
                                            Sin notificaciones
                                        </div>
                                    ) : (
                                        notifications.slice(0, 10).map((notif) => (
                                            <DropdownMenuItem
                                                key={notif.id}
                                                className={cn(
                                                    'flex flex-col items-start p-3 cursor-pointer',
                                                    !notif.leida && 'bg-primary/5'
                                                )}
                                            >
                                                <span className="font-medium text-sm">{notif.titulo}</span>
                                                <span className="text-xs text-muted-foreground line-clamp-2 mt-1">
                                                    {notif.mensaje}
                                                </span>
                                            </DropdownMenuItem>
                                        ))
                                    )}
                                </ScrollArea>
                            </DropdownMenuContent>
                        </DropdownMenu>

                        {/* User Menu */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="gap-2" data-testid="user-menu-btn">
                                    <User className="h-5 w-5" />
                                    <span className="hidden md:inline">{user?.nombre}</span>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuLabel>
                                    <div>
                                        <p className="font-medium">{user?.nombre}</p>
                                        <p className="text-xs text-muted-foreground">{user?.email}</p>
                                    </div>
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem>
                                    <Badge variant="outline" className="uppercase text-xs">
                                        {getRolLabel(user?.rol)}
                                    </Badge>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                    onClick={handleLogout} 
                                    className="text-destructive cursor-pointer focus:text-destructive focus:bg-destructive/10"
                                    data-testid="logout-menu-item"
                                >
                                    <LogOut className="h-4 w-4 mr-2" />
                                    Cerrar Sesión
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </header>

                {/* Page Content */}
                <main className="pt-16 lg:pt-16 min-h-screen">
                    <div className="p-4 md:p-6 lg:p-8">
                        {children}
                    </div>
                </main>
                <GuiasModuleAddons />
            </div>
        </div>
    );
};

// Addons que SOLO aplican al módulo Guías (banner PWA + auto-subscribe push).
// Aislado para no afectar otros módulos que reusen Layout o tengan su propio.
const GuiasModuleAddons = () => {
    const location = useLocation();
    const isGuias = location.pathname.startsWith('/guias');
    // Hook con suscripción silenciosa si ya hay permiso
    useGuiasPushSubscription({ enabled: isGuias });
    if (!isGuias) return null;
    return <PWAInstallBanner />;
};

export default Layout;
